import "./CostCalculationTable.scss";

import { Grid, GridSize, Typography } from "@material-ui/core";
import React, { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { CostCalculationPercentage } from "../../backend/generated/cost-calculation-percentage-model";
import { CostCalculationPercentageType } from "../../backend/types/utils";
import { Mode } from "../../utils/utility";
import { CostCalculationPercentageField, CostCalculationPercentageInputTypeKey } from "../input/fields/CostCalculationPercentageField";
import { SubformProps } from "../SubformProps";

const xs = (
  mode: Mode, min:number, max: number
) : GridSize => 12 / (mode === Mode.View ? max : min) as GridSize;

enum ValueVisibility {
  All = "all",
  Main = "main",
  Annual = "annual",
  Invisible = "invisible",
}

enum Column {
  Main = "main",
  Annual = "annual",
}

const valueVisibilityColumnMapping = {
  [Column.Main]: ValueVisibility.Main,
  [Column.Annual]: ValueVisibility.Annual,
}

const isVisible = (
  visibility: any, column: Column
) => {
  return !visibility || visibility === ValueVisibility.All || visibility === valueVisibilityColumnMapping[column];
}

type CostCalculationPercentageTypeToValueVisibilityMapping = {
  [key in CostCalculationPercentageType]: ValueVisibility;
};

const valueVisibility : CostCalculationPercentageTypeToValueVisibilityMapping = {
  [CostCalculationPercentageType.MONETARY_CONTRIBUTIONS]: ValueVisibility.Main,
  [CostCalculationPercentageType.THEREOF_FACILITATOR]: ValueVisibility.Main,
  [CostCalculationPercentageType.BROKERAGE_COSTS]: ValueVisibility.All,
  [CostCalculationPercentageType.THEREOF_CONTRIBUTIONS]: ValueVisibility.All,
  [CostCalculationPercentageType.FINANCIAL_INSTITUTION_COST]: ValueVisibility.All,
  [CostCalculationPercentageType.FINANCIAL_INSTRUMENT_COST]: ValueVisibility.All,
  [CostCalculationPercentageType.COMPLETE_PERIOD_COST]: ValueVisibility.All,
  [CostCalculationPercentageType.REDUCED_RETURN_FIRST_YEAR]: ValueVisibility.Main,
  [CostCalculationPercentageType.REDUCED_RETURN_FOLLOWING_YEARS]: ValueVisibility.Main,
}

interface AbsoluteValueProps extends SubformProps<Array<CostCalculationPercentage>> {
  type: CostCalculationPercentageType;
  id: string;
  amount: number;
}

const AbsoluteValue = (props: AbsoluteValueProps) => {
  const {
    type,
    id,
    values,
    amount,
  } = props;

  const index = values.findIndex((obj: CostCalculationPercentage) => obj.type === type);

  return (
    isVisible(
      valueVisibility[type],
      id as Column
    ) ?
      <Fragment>
        {amount * values[index][id] / 100} €
      </Fragment>
      :
      <></>
  );
}

interface RelativeValueProps extends SubformProps<Array<CostCalculationPercentage>> {
  mode: Mode;
  type: CostCalculationPercentageType;
  id: string;
}

const RelativeValue = (props: RelativeValueProps) => {
  const {
    mode,
    type,
    id,
    values,
    errors,
    touched,
    setFieldValue,
  } = props;

  const index = values.findIndex((obj: CostCalculationPercentage) => obj.type === type);

  if (isVisible(
    valueVisibility[type],
    id as Column
  )) {
    switch (mode) {
      case Mode.Edit:
        return (
          <CostCalculationPercentageField
            id={id as CostCalculationPercentageInputTypeKey}
            values={values[index]}
            errors={errors[index]}
            touched={touched[index]}
            setFieldValue={(
              id: string, value: number
            ) => setFieldValue(
              type,
              id,
              value
            )}
            setValues={() => console.log('NOT SUPPORTED')}
          />
        );
      case Mode.View:
        return (
          <Fragment>
            {values[index][id]} %
          </Fragment>
        );
      default:
        return <></>;
    }
  } else {
    return <></>;
  }
}

enum TemplateSetArgumentType {
  One,
  Two,
  Three,
  Four,
}

interface BaseSetProps {
  mode: Mode;
  xs: GridSize;
}

interface TemplateSetProps extends BaseSetProps {
  args: Map<TemplateSetArgumentType, JSX.Element>;
  cells: 2 | 4;
}

const TemplateSet = (props: TemplateSetProps) => {
  const {
    mode,
    args,
    cells,
  } = props;

  const size = cells === 4 ? xs(
    mode,
    2,
    4
  ) : 6;

  const itemClass = cells === 4 ? "cost-item--centered cost-item--bordered small-text" : "cost-item--centered small-text";

  return (
    <Grid container direction="row" item xs={props.xs}>
      {cells === 4 && mode === Mode.View &&
      <Grid item className={itemClass} xs={size}>
        {args.get(TemplateSetArgumentType.One)}
      </Grid>
      }

      <Grid item className={itemClass} xs={size}>
        {args.get(TemplateSetArgumentType.Two)}
      </Grid>

      {cells === 4 && mode === Mode.View &&
      <Grid item className={itemClass} xs={size}>
        {args.get(TemplateSetArgumentType.Three)}
      </Grid>
      }

      <Grid item className={itemClass} xs={size}>
        {args.get(TemplateSetArgumentType.Four)}
      </Grid>
    </Grid>
  );
}

interface AmountSetProps extends BaseSetProps {
  amount: number;
}

const AmountSet = (props: AmountSetProps) =>
  <TemplateSet
    cells={4}
    args={new Map<TemplateSetArgumentType, JSX.Element>([
      [
        TemplateSetArgumentType.One,
        <>{props.amount} €</>
      ]
    ])}
    {...props}
  />

const ValueTypesSet = (props: BaseSetProps) => {
  // Hooks
  const { t } = useTranslation();

  return (
    <TemplateSet
      cells={4}
      args={new Map<TemplateSetArgumentType, JSX.Element>([
        [
          TemplateSetArgumentType.One,
          <Typography color="textSecondary">
            {t("Absolute value")}
          </Typography>
        ],
        [
          TemplateSetArgumentType.Two,
          <Typography color="textSecondary">
            %-{t("value")}
          </Typography>
        ],
        [
          TemplateSetArgumentType.Three,
          <Typography color="textSecondary">
            {t("Absolute value")}
          </Typography>
        ],
        [
          TemplateSetArgumentType.Four,
          <Typography color="textSecondary">
            %-{t("value")}
          </Typography>
        ]
      ])}
      {...props}
    />
  );
}

interface CostTypeSetProps extends BaseSetProps {
  variation: 'time' | 'total';
}

const CostTypeSet = (props: CostTypeSetProps) => {
  // Hooks
  const { t } = useTranslation();

  const { variation } = props;

  return (
    <TemplateSet
      cells={2}
      args={new Map<TemplateSetArgumentType, JSX.Element>([
        [
          TemplateSetArgumentType.Two,
          <Typography color="textSecondary">
            {variation === 'time' ? t("One-off costs") : t("Complete period")}
          </Typography>
        ],
        [
          TemplateSetArgumentType.Four,
          <Typography color="textSecondary">
            {variation === 'time' ? t("Ongoing charges p.a.") : t("p.a.")}
          </Typography>
        ]
      ])}
      {...props}
    />
  );
}

interface DataSetProps extends BaseSetProps, SubformProps<Array<CostCalculationPercentage>> {
  mode: Mode;
  amount: number;
  type: CostCalculationPercentageType;
}

const DataSet = (props: DataSetProps) =>
  <TemplateSet
    cells={4}
    args={new Map<TemplateSetArgumentType, JSX.Element>([
      [
        TemplateSetArgumentType.One,
        <AbsoluteValue
          {...props}
          id="main"
        />
      ],
      [
        TemplateSetArgumentType.Two,
        <RelativeValue
          {...props}
          id="main"
        />
      ],
      [
        TemplateSetArgumentType.Three,
        <AbsoluteValue
          {...props}
          id="annual"
        />
      ],
      [
        TemplateSetArgumentType.Four,
        <RelativeValue
          {...props}
          id="annual"
        />
      ]
    ])}
    {...props}
  />

interface DataRowProps extends SubformProps<Array<CostCalculationPercentage>> {
  mode: Mode;
  amount: number;
  type: CostCalculationPercentageType;
}

const DataRow = (props: DataRowProps) => {
  // Hooks
  const { t } = useTranslation();

  const {
    mode,
    type,
  } = props;

  const getText = () => {
    switch (type) {
      case CostCalculationPercentageType.MONETARY_CONTRIBUTIONS:
        return t("Monetary contributions in relation to investment amount to institute");
      case CostCalculationPercentageType.THEREOF_FACILITATOR:
        return t("Thereof to facilitator");
      case CostCalculationPercentageType.BROKERAGE_COSTS:
        return t("Brokerage costs");
      case CostCalculationPercentageType.THEREOF_CONTRIBUTIONS:
        return t("Thereof contributions");
      case CostCalculationPercentageType.FINANCIAL_INSTITUTION_COST:
        return t("Costs of financial institution");
      case CostCalculationPercentageType.FINANCIAL_INSTRUMENT_COST:
        return t("Total cost of the financial instrument");
      case CostCalculationPercentageType.COMPLETE_PERIOD_COST:
        return t("Total costs over 5 years");
      case CostCalculationPercentageType.REDUCED_RETURN_FIRST_YEAR:
        return t("Costs reduce the return in the first year by");
      case CostCalculationPercentageType.REDUCED_RETURN_FOLLOWING_YEARS:
        return t("Costs reduce the return in the following years by");
      default:
        return "";
    }
  }

  const size = xs(
    mode,
    2,
    2
  );

  return (
    <Grid container direction="row">
      <Grid item className="cost-item--bordered" xs={size}>
        <Typography className="bold text" color="textSecondary">
          {getText()}
        </Typography>
      </Grid>

      <DataSet {...props} xs={size} />
    </Grid>
  );
}

export interface CostCalculationTableProps extends SubformProps<Array<CostCalculationPercentage>> {
  mode: Mode;
  amount: number;
}

export const CostCalculationTable = (props: CostCalculationTableProps) => {
  // Hooks
  const { t } = useTranslation();

  const { mode } = props;

  const size = xs(
    mode,
    2,
    2
  );

  return (
    <Grid container direction="column" className="cost-calculation-table">
      <Grid container direction="row">
        <Grid item className="cost-item--bordered" xs={size}>

        </Grid>

        <Grid item className="cost-item--centered cost-item--bordered" xs={size}>
          <Typography className="subtitle--bold subtitle--inner" color="textSecondary">
            {mode === Mode.View ? t("Your investment") : ""}
          </Typography>
        </Grid>
      </Grid>

      {mode === Mode.View &&
      <Grid container direction="row">
        <Grid item className="cost-item--bordered" xs={size}>
          <Typography className="bold text" color="textSecondary">
            {t("Investment amount")}
          </Typography>
        </Grid>

        <AmountSet {...props} xs={size} />
      </Grid>
      }

      <DataRow {...props} type={CostCalculationPercentageType.MONETARY_CONTRIBUTIONS}/>
      <DataRow {...props} type={CostCalculationPercentageType.THEREOF_FACILITATOR}/>

      <Grid container direction="row">
        <Grid item className="cost-item"/>
      </Grid>

      <Grid container direction="row">
        <Grid item className="cost-item" xs={size} />

        <CostTypeSet {...props} xs={size} variation="time" />
      </Grid>

      <Grid container direction="row">
        <Grid item className="cost-item--bordered" xs={size} >
          <Typography className="bold" color="textSecondary">
            {t("Types of fees and costs")}
          </Typography>
        </Grid>

        <ValueTypesSet {...props} xs={size} />
      </Grid>

      <DataRow {...props} type={CostCalculationPercentageType.BROKERAGE_COSTS}/>
      <DataRow {...props} type={CostCalculationPercentageType.THEREOF_CONTRIBUTIONS}/>
      <DataRow {...props} type={CostCalculationPercentageType.THEREOF_CONTRIBUTIONS}/>
      <DataRow {...props} type={CostCalculationPercentageType.FINANCIAL_INSTITUTION_COST}/>
      <DataRow {...props} type={CostCalculationPercentageType.FINANCIAL_INSTRUMENT_COST}/>

      <Grid container direction="row">
        <Grid item className="cost-item">
          <Typography color="textSecondary">
            {}
          </Typography>
        </Grid>
      </Grid>

      <Grid container direction="row">
        <Grid item className="cost-item" xs={size} />

        <CostTypeSet {...props} xs={size} variation="total" />
      </Grid>

      <DataRow {...props} type={CostCalculationPercentageType.COMPLETE_PERIOD_COST}/>
      <DataRow {...props} type={CostCalculationPercentageType.REDUCED_RETURN_FIRST_YEAR}/>
      <DataRow {...props} type={CostCalculationPercentageType.REDUCED_RETURN_FOLLOWING_YEARS}/>
    </Grid>
  );
};

CostCalculationTable.defaultProps = {
  amount: 0,
  errors: {},
  touched: {},
  setFieldValue: null,
  setValues: null
};
